import React, { Suspense,useContext, useEffect, useLayoutEffect, useState } from 'react';
import AIAnalysisTab from './costBenchmarkComponents/AIAnalysisTab.js';
import ReusableScaffold from '../reusable/ReusableScaffold/ReusableScaffold.js';
import { BODY_SLOT } from '../PBBStrategist/pbbStrategistHelpers.js';
import { messageManager } from '../System/messageManager/messageManager.js';
import { getCostBenchmarkHeaderButtons, getDisabledButtonTooltip, getTabButtons, onExportExcel, onExportPdf, removeUnusedKeysFromPrograms } from './costBenchmarkHelpers.js';
import { ForgeBadge, ForgeButton, ForgeCard, ForgeIcon, ForgeToolbar } from '@tylertech/forge-react';
import BoundariesModal from './costBenchmarkModals/boundariesModal.js';
import GraphicTab from './costBenchmarkComponents/GraphicTab.js';
import ProgramTab from './costBenchmarkComponents/ProgramTab.js';
import PaginationWrapper from '../reusable/Paginator/PaginationWrapper.js';
import './CostBenchmark.scss';
import { FILTERS, PROGRAM } from '../../static/keyWords.js';
import { COST_BENCHMARK_PAGE_LENGTH, PDF } from './costBenchmarkTexts.js';
import socket from '../../components/System/Socket/socket.js';
import ToolbarTitle from '../reusable/Toolbars/ToolbarHelperComponents/ToolbarTitle.js';
import { FiltersContext } from '../reusable/Filters/filterContext.js';
import ReusableFilterDrawer from '../reusable/Filters/ReusableFilterDrawer.js';
import { FILTER_TYPES } from '../reusable/Filters/filterHelpers.js';
import { CostBenchmarkContext } from '../../contexts/costBenchmarkingContext.js';
import { getPopulationBoundaries } from '../../services/organizationService.js';
import ScatterChartD3 from './costBenchmarkComponents/ScatterChart.js';
import { useUser } from '../../contexts/userContext.js';
import { useNameConventions } from '../../contexts/nameConventionsContext.js';
import { LoaderContext } from '../../contexts/loaderContext.js';
import { PLURAL_FORM_ALIAS } from '../NameConvention/nameConventionHelpers.js';
import { Context } from '../../store/Store.js';
import { REDUCER_ACTION_TYPES } from '../../store/Reducer.js';
import ValidationWrapper from '../reusable/Validations/ValidationWrapper.js';

const CostBenchmark = () => {
  const [openBoundariesDialog, setOpenBoundariesDialog] = useState(false);
  const { setOpenFilters, numOfAppliedFilters } = useContext(FiltersContext);
  const { setCurrPage, setPopulationBoundaries, matchSettings, checkBudgetPrograms, populationBoundaries, fetchGraphicTabData, activeTab, setActiveTab, currPage, goToPage, filteredData } = useContext(CostBenchmarkContext);
  const [mounted, setMounted] = useState(false);
  const { user } = useUser();
  const { nameConventions } = useNameConventions();
  const { setLoading } = useContext(LoaderContext);
  const [state, dispatch] = useContext(Context);

  const disableExportOptions = state.generatingDocument || !filteredData.length;
  const disabledButtonTooltip = getDisabledButtonTooltip(state.generatingDocument, !filteredData.length);

  const fetchPopulationBoundaryData = async () => {
    try {
      const { data } = await getPopulationBoundaries();
      setPopulationBoundaries(data);
    } catch {
      // error already handled in axios interceptor
    }
  };

  const toggleBoundariesDialog = () => setOpenBoundariesDialog(prev => !prev);

  const handleTabChange = async (tab) => {
    setCurrPage(0);
    setActiveTab(tab);
  };

  const onExport = async (fileType = PDF) => {
    if (!socket.connected) socket.connect();
    dispatch({ type: REDUCER_ACTION_TYPES.GENERATING_DOCUMENT, payload: true });
    const { cleanedData } = removeUnusedKeysFromPrograms(filteredData);

    if (fileType === PDF) { onExportPdf(cleanedData, user, matchSettings, populationBoundaries.population, socket); }
    else { onExportExcel(cleanedData, user, matchSettings, populationBoundaries, socket); }
  };

  const loadActiveTab = () => {
    switch (activeTab) {
      case 0:
        return (
          <>
            <ForgeToolbar>
              <ToolbarTitle title={messageManager(nameConventions).COST_BENCHMARK.GRAPHIC_PAGE_CARD_TITLE} />
              <ForgeButton
                id={'filters'}
                slot="end"
                alt="filter"
                onClick={() => setOpenFilters(true)}
              >
                <ForgeIcon external externalType="all" name="filter_list" />
                {FILTERS}
                {numOfAppliedFilters > 0 && (
                  <ForgeBadge>{numOfAppliedFilters}</ForgeBadge>
                )}
              </ForgeButton>
            </ForgeToolbar>
            <div className="tabContainer scrollContainer">
              <GraphicTab />
            </div>
          </>

        );
      case 1:
        return (<>
          <ForgeToolbar>
            <ToolbarTitle title={nameConventions?.[PROGRAM]?.[PLURAL_FORM_ALIAS] || PROGRAM}
            />
            <ForgeButton
              id={'filters'}
              slot="end"
              alt="filter"
              onClick={() => setOpenFilters(true)}
            >
              <ForgeIcon external externalType="all" name="filter_list" />
              {FILTERS}
              {numOfAppliedFilters > 0 && (
                <ForgeBadge>{numOfAppliedFilters}</ForgeBadge>
              )}
            </ForgeButton>
          </ForgeToolbar>
          <div className="tabContainer scrollContainer">
            <ProgramTab />
          </div>
          <PaginationWrapper
            topDivider
            totalCount={filteredData?.length || 0}
            pageSize={COST_BENCHMARK_PAGE_LENGTH}
            currentPage={currPage}
            handleChange={(e) => goToPage(e?.detail?.pageIndex)}
          />
        </>);
      case 2:
        return (<>
          <ForgeToolbar>
            <ToolbarTitle title={'AI-Powered Analysis'}
            />
            <ForgeButton
              id={'filters'}
              slot="end"
              alt="filter"
              onClick={() => setOpenFilters(true)}
            >
              <ForgeIcon external externalType="all" name="filter_list" />
              {FILTERS}
              {numOfAppliedFilters > 0 && (
                <ForgeBadge>{numOfAppliedFilters}</ForgeBadge>
              )}
            </ForgeButton>
          </ForgeToolbar>
          <div className="tabContainer scrollContainer">
            <AIAnalysisTab />
          </div>
        </>);
      default:
        return null;
    }
  };

  useLayoutEffect(() => {
    if (!mounted) {
      setLoading(true);
      setMounted(prev => !prev);
    }
  }, [user?.activeBudget, user?.activeOrganization, user?.activeCostCenters, mounted]);

  useEffect(() => {
    fetchPopulationBoundaryData();
    fetchGraphicTabData();
    checkBudgetPrograms(user?.activeBudget?.id);
  }, [user?.activeBudget, user?.activeOrganization, user?.activeCostCenters]);

  return (
    <ValidationWrapper validatePrograms>
      <>
        {/* we need this so we can have the graphic data on the both tabs. */}
        <div
          id="export-chart-container"
          style={{
            position: 'fixed',
            left: '-10000px',
            top: 0,
            width: '1200px',
            height: '800px',
            pointerEvents: 'none',
            opacity: 0,
            zIndex: -9999
          }}
        >
          <ScatterChartD3 data={filteredData} />
        </div>
        <ReusableScaffold
          headerTabButtons={getTabButtons(nameConventions)}
          handleTabChange={handleTabChange}
          activeTab={activeTab}
          title={messageManager(nameConventions).COST_BENCHMARK.TITLE}
          headerButtons={getCostBenchmarkHeaderButtons(toggleBoundariesDialog, onExport, disableExportOptions, disabledButtonTooltip)}
        >
          <div slot={BODY_SLOT} className="standardMarginWithTabs scrollContainer">
            <ForgeCard no-padding className="fullHeight scrollContainer" >
              <div className="fullHeight flexContainer direction-column scrollContainer">
                {loadActiveTab()}
              </div>
            </ForgeCard>
          </div >
        </ReusableScaffold >
        {
          openBoundariesDialog && (
            <Suspense>
              <BoundariesModal onClose={toggleBoundariesDialog} onSubmit={fetchGraphicTabData} open={openBoundariesDialog} />
            </Suspense>
          )
        }
        <ReusableFilterDrawer filters={[FILTER_TYPES.DEPARTMENT, FILTER_TYPES.COST_BENCHMARK_COST]} />
      </>
    </ValidationWrapper>
  );
};

export default CostBenchmark;
